/**
 * lib/ThemeContext.tsx
 * ---------------------
 * What: Theme provider and hook. Supplies palette tokens, theme variants, and
 * type (light/dark/blueNeon) to components.
 * Why: Centralised theming makes it easy to maintain consistent styling across
 * platforms and switch themes dynamically.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Appearance } from 'react-native';
import { safeStorage } from '../utils/storage';
import { useConfig } from './ConfigContext';
import { getLightTheme, getDarkTheme, getBlueNeonTheme } from './themeVariants';

export type ThemeType = 'automatic' | 'light' | 'dark' | 'blueNeon';

export const lightTheme = {
  background: '#F5F6FA',
  card: '#FFFFFF',
  text: '#1A1A1A',
  secondaryText: '#666',
  accent: '#007AFF',
  border: '#E0E0E0',
  inputBackground: '#F0F0F0',
  inputBorder: '#D1D1D1',
  modalOverlay: 'rgba(0,0,0,0.2)',
  modalBackground: '#FFF',
  buttonText: '#fff',
  buttonBackground: '#007AFF',
  selected: '#007AFF',
  selectedText: '#FFF',
  error: '#ff3b30',
  success: '#34C759',
};

export const darkTheme = {
  background: '#1a1a1a',
  card: '#2a2a2a',
  text: '#fff',
  secondaryText: '#999',
  accent: '#007AFF',
  border: '#333',
  inputBackground: '#222',
  inputBorder: '#333',
  modalOverlay: 'rgba(0,0,0,0.5)',
  modalBackground: '#2a2a2a',
  buttonText: '#fff',
  buttonBackground: '#007AFF',
  selected: '#007AFF',
  selectedText: '#fff',
  error: '#ff3b30',
  success: '#34C759',
};

// Blue Neon theme (formerly “Test Theme”)
export const blueNeonTheme = {
  background: '#13274c', // neutral-800
  card: '#1f3255',       // neutral-750
  text: '#f3f4f6',       // neutral-50
  secondaryText: '#d0d4db',
  accent: '#23ffff',      // teal-200
  border: '#374867',
  inputBackground: '#2b3d5e',
  inputBorder: '#374867',
  modalOverlay: 'rgba(0,0,0,0.5)',
  modalBackground: '#1f3255',
  buttonText: '#13274c',
  buttonBackground: '#23ffff',
  selected: '#23ffff',
  selectedText: '#13274c',
  error: '#ff4d3c',
  success: '#00f55f',
};

const ThemeContext = createContext({
  themeType: 'automatic' as ThemeType,
  setThemeType: (type: ThemeType) => {},
  theme: darkTheme,
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { config } = useConfig();

  const [themeType, setThemeType] = useState<ThemeType>('automatic');
  const [theme, setTheme] = useState(getDarkTheme(config.theme.darkVersion));

  console.log('🎨 [ThemeProvider] Provider rendered with themeType:', themeType);

  // Load theme preference from localStorage (web) or AsyncStorage (native)
  useEffect(() => {
    const loadTheme = async () => {
      try {
        console.log('🎨 [ThemeProvider] Loading theme preference from storage');
        const stored = typeof window !== 'undefined'
          ? safeStorage.getItem('themeType')
          : null;
        console.log('🎨 [ThemeProvider] Stored theme preference:', stored);
        if (stored === 'test') {
          // migrate old preference
          setThemeType('blueNeon');
        } else if (stored === 'light' || stored === 'dark' || stored === 'automatic' || stored === 'blueNeon') {
          console.log('🎨 [ThemeProvider] Setting theme type from storage:', stored);
          setThemeType(stored);
        }
      } catch (error) {
        console.error('❌ [ThemeProvider] Error loading theme preference:', error);
      }
    };
    loadTheme();
  }, []);

  // Save theme preference
  useEffect(() => {
    console.log('💾 [ThemeProvider] Saving theme preference to storage:', themeType);
    try {
      if (typeof window !== 'undefined') {
        safeStorage.setItem('themeType', themeType);
      }
    } catch (err) {
      console.warn('💾 [ThemeProvider] Failed to persist theme preference:', err);
    }
  }, [themeType]);

  // Update theme based on preference
  useEffect(() => {
    console.log('🎨 [ThemeProvider] Updating theme based on preference:', themeType);
    console.log('🔍 [ThemeProvider] Remote theme versions', config.theme);
    let resolvedTheme;
    if (themeType === 'light') {
      resolvedTheme = getLightTheme(config.theme.lightVersion);
    } else if (themeType === 'dark') {
      resolvedTheme = getDarkTheme(config.theme.darkVersion);
    } else if (themeType === 'blueNeon') {
      resolvedTheme = getBlueNeonTheme(config.theme.blueNeonVersion);
    } else {
      // Automatic: follow system
      const colorScheme = Appearance.getColorScheme?.() || 'dark';
      console.log('🎨 [ThemeProvider] Automatic theme, system color scheme:', colorScheme);
      resolvedTheme = colorScheme === 'light' ? getLightTheme(config.theme.lightVersion) : getDarkTheme(config.theme.darkVersion);
    }
    console.log('🎨 [ThemeProvider] Setting resolved theme:', themeType, 'with palette', resolvedTheme);
    setTheme(resolvedTheme);
  }, [themeType, config]);

  // Listen for system theme changes if automatic
  useEffect(() => {
    if (themeType !== 'automatic') {
      console.log('🎨 [ThemeProvider] Not automatic theme, skipping system listener');
      return;
    }
    console.log('🎨 [ThemeProvider] Setting up system theme change listener');
    const listener = Appearance.addChangeListener?.(({ colorScheme }) => {
      console.log('🎨 [ThemeProvider] System theme changed to:', colorScheme);
      setTheme(colorScheme === 'light' ? getLightTheme(config.theme.lightVersion) : getDarkTheme(config.theme.darkVersion));
    });
    return () => {
      if (listener && listener.remove) {
        console.log('🎨 [ThemeProvider] Removing system theme change listener');
        listener.remove();
      }
    };
  }, [themeType, config]);

  console.log('📤 [ThemeProvider] Returning context with themeType:', themeType);

  return (
    <ThemeContext.Provider value={{ themeType, setThemeType, theme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  console.log('🎨 [useTheme] Hook called, returning context with themeType:', context.themeType);
  return context;
}; 